import { createContext, memo, type ReactNode, useContext, useMemo } from 'react';

import { useSavedSizesCatalog } from '@air/feature-saved-sizes/use-saved-sizes-catalog';
import { type SavedSize, type SizeSet } from '@air/feature-saved-sizes/utils';
import { FIVE_MINUTES } from '@air/utilities';
import { reportErrorToBugsnag } from '@air/utils-error';

import { usePrivateContextSavedSizesPermissions } from '~/components/v2/hooks/usePrivateContextSavedSizesPermissions';
import { type EditInCanvasSizeSet } from '~/components/v2/store/features/canvas/usePendingCanvasEditStore';
import { useTemporaryFeatureFlags } from '~/hooks/useTemporaryFeatureFlags';
import { useCurrentWorkspace } from '~/providers/CurrentWorkspaceProvider';
import { isDevOrTestStage } from '~/swr-hooks/utils';
import { pickMostRecentSizeSet } from '~/utils/canvases/pickMostRecentSizeSet';

export type PrivateSavedSizesCatalogContextValue = {
  /**
   * The set "Resize for [set name]" offers, derived once here rather than in every menu that reads it.
   * Null when saved sizes are unavailable, no set qualifies, or either list has not loaded yet or has failed (a
   * failed background refetch counts; one still in flight does not), so menus opened then report the set as not eligible.
   */
  editInCanvasSizeSet: EditInCanvasSizeSet | null;
  isError: boolean;
  isLoading: boolean;
  /** Saved sizes are rolled out and the user can view them. When false, the lists are empty and nothing loads. */
  isSavedSizesAvailable: boolean;
  sizePresets: SavedSize[];
  sizeSets: SizeSet[];
};

export type PrivateSavedSizesCatalogProviderProps = {
  children: ReactNode;
};

const NO_SIZE_PRESETS: SavedSize[] = [];
const NO_SIZE_SETS: SizeSet[] = [];

const defaultContextValue: PrivateSavedSizesCatalogContextValue = {
  editInCanvasSizeSet: null,
  isError: false,
  isLoading: false,
  isSavedSizesAvailable: false,
  sizePresets: NO_SIZE_PRESETS,
  sizeSets: NO_SIZE_SETS,
};

export const PrivateSavedSizesCatalogContext = createContext<PrivateSavedSizesCatalogContextValue>(defaultContextValue);

export const usePrivateSavedSizesCatalogContext = () => {
  const ctx = useContext(PrivateSavedSizesCatalogContext);

  if (ctx === defaultContextValue) {
    const error = new Error('usePrivateSavedSizesCatalogContext must be used within PrivateSavedSizesCatalogProvider');

    if (isDevOrTestStage()) {
      throw error;
    }

    reportErrorToBugsnag({
      error,
      context: 'Private saved sizes catalog context not found',
    });
  }

  return ctx;
};

/**
 * Loads the current workspace's saved sizes and size sets once for the private layout. Not gated on
 * `editInCanvasResizeForSetEnabled`: control users need the same set to report `size_set_eligible`.
 */
export const PrivateSavedSizesCatalogProvider = memo(({ children }: PrivateSavedSizesCatalogProviderProps) => {
  const { savedSizesEnabled } = useTemporaryFeatureFlags();
  const { canView: canViewSavedSizes } = usePrivateContextSavedSizesPermissions();
  const { currentWorkspace } = useCurrentWorkspace();
  const workspaceId = currentWorkspace?.id;
  const isSavedSizesAvailable = savedSizesEnabled && canViewSavedSizes;

  // Every saved-sizes mutation invalidates these lists, so the long stale time only skips refetches on remount.
  const { isError, isLoading, sizePresets, sizeSets } = useSavedSizesCatalog({
    enabled: isSavedSizesAvailable && !!workspaceId,
    staleTime: FIVE_MINUTES,
    workspaceId,
  });

  // `enabled: false` still serves cached data, so availability is re-checked here rather than trusting the lists.
  const contextValue = useMemo<PrivateSavedSizesCatalogContextValue>(
    () => ({
      // Sets reference saved sizes from the other list, so a set picked from a half-loaded catalog drops those sizes.
      editInCanvasSizeSet:
        isSavedSizesAvailable && !isLoading && !isError ? pickMostRecentSizeSet({ sizePresets, sizeSets }) : null,
      isError: isSavedSizesAvailable && isError,
      isLoading: isSavedSizesAvailable && isLoading,
      isSavedSizesAvailable,
      sizePresets: isSavedSizesAvailable ? sizePresets : NO_SIZE_PRESETS,
      sizeSets: isSavedSizesAvailable ? sizeSets : NO_SIZE_SETS,
    }),
    [isError, isLoading, isSavedSizesAvailable, sizePresets, sizeSets],
  );

  return (
    <PrivateSavedSizesCatalogContext.Provider value={contextValue}>{children}</PrivateSavedSizesCatalogContext.Provider>
  );
});

PrivateSavedSizesCatalogProvider.displayName = 'PrivateSavedSizesCatalogProvider';
